<script setup lang="ts">
import Image from "~/components/atoms/Image.vue";
import Text from "~/components/atoms/Text.vue";
import ProductPrice from "~/components/molecules/ProductPrice.vue";
import Button from "~/components/atoms/Button.vue";
import AlternativeCartIcon from "~/components/icons/AlternativeCartIcon.vue";
import PlusIcon from "~/components/icons/PlusIcon.vue";
import {useCartStore} from "~/store/cartStore";
import {useUserStore} from "~/store/userStore";
import {PRODUCT_VIEW_BLOCKS, saveProductViewContext} from "~/utils/productViewContext.js";

const store = useCartStore()
const userStore = useUserStore()
const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  vertical: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['click'])

const handleClick = (e: Event) => {
  if (props.product.type === 'supplementary_bundle') {
    e.preventDefault()
    emits('click', props.product)
    return
  }

  if (props.product.url) {
    saveProductViewContext({
      productId: props.product.id,
      block: PRODUCT_VIEW_BLOCKS.betterWith,
    })
  }
}

const showReadyToSell = computed(() => {
  return userStore.isAdmin
      && userStore.showAdmin
      && props.product.type === 'bundle'
      && props.product.ready_to_sell != null
})

const readyToSellLabel = computed(() => {
  const value = Number(props.product.ready_to_sell)
  const qty = Number.isFinite(value)
      ? (Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100))
      : String(props.product.ready_to_sell)
  const unit = props.product.unit || 'st'

  return `${qty} ${unit}`
})

const component = computed(() => {
  if (props.product.type !== 'supplementary_bundle' && props.product.url) return defineNuxtLink({})
  return 'div'
})
</script>

<template>
  <component :is="component" @click="handleClick"
             :to="'/shop/'+props.product.url+'/'"
             @focusin.stop
             class="product full-width" :class="{clickable:props.product.url, vertical}">
    <div class="g-4">
      <div class="image full-width aspect-1">
                <Image :src="props.product.image"/>
      </div>

      <div class="g-4">
        <Text tag="strong" v-if="props.product.type === 'supplementary_bundle'" class="title suppl-bundle">{{
            props.product.name
          }}
        </Text>
        <p v-else class="title" v-html="props.product.bold_name"/>

        <Text class="description" v-if="props.product.type === 'supplementary_bundle' && props.product.description">
          {{ props.product.description }}
        </Text>

        <p v-if="showReadyToSell" class="ready-to-sell">Ready to Sell: {{ readyToSellLabel }}</p>

        <div class="row flex-center full-width g-4 price">
          <template v-if="props.product.type === 'supplementary_bundle'">
            <Text tag="strong">{{ $t('product.from_short') }}</Text>
            <ProductPrice :digits="0" :price="props.product.price"/>
          </template>
          <template v-else-if="props.product.hc_volume_price && props.product.url">
            <ProductPrice :digits="0" :price="props.product.hc_volume_price"/>
            <ProductPrice :digits="0" :price="props.product.hc_standard_price" type="old"/>
          </template>
        </div>
      </div>

      <div class="bundle-count row" v-if="props.product.type === 'bundle'">
        <b>{{ props.product.qty }} {{ props.product.unit || 'st' }}</b>
      </div>
      <Button v-else-if="props.product.type === 'supplementary_bundle'" class="buy" type="bordered">
        <PlusIcon stroke="black" width="16" height="16"/>
      </Button>
      <Button v-else class="buy"
              @click.stop.prevent="store.addItemToCart({id: product.id, count: 1,source:store.sources.better_together_combo})"
              type="bordered">
        <AlternativeCartIcon width="16" height="16"/>
      </Button>
    </div>
  </component>
</template>

<style scoped lang="scss">
.product {
  background: $white;
  width: 160px;
  min-height: 318px;
  max-height: none;
  padding-bottom: 10px;
  display: flex;
  height: auto;

  @include smd {
    margin-right: 10px;
  }

  .title {
    font-size: 11px;
    height: fit-content;

    &.suppl-bundle {
      font-size: 14px;
    }
  }

  .image {
    max-width: 160px;
    max-height: 160px;
  }

  .price {
    font-size: 13px;
  }

  .description {
    font-size: 12px;
    min-height: 36px;
  }

  .buy {
    width: 85%;
    margin-left: auto;
    margin-right: auto;
    margin-top: auto;
    border: .5px solid #d7d7d7;
    border-radius: 5px;

    &:deep(svg) {
      stroke-width: 1.5px;
    }
  }

  .ready-to-sell {
    margin: 4px 8px 0;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    text-align: center;
    text-decoration: underline;
  }

  .bundle-count {
    text-align: center;
    font-size: 13px;
    transform: translateY(10px);
    margin: auto;

    b {
      min-width: 40px;
      height: 21px;
      background-color: #ff5959;
      color: $white;
    }
  }

  &.vertical {
    max-width: 100%;
    width: 100%;
    min-height: 83px;
    height: 83px;
    padding: 12px;
    border: 1px solid $border-grey;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: hsla(0, 0%, 97.6%, .9);
    }

    div:first-child {
      flex-direction: row;
      align-items: center;
      gap: 16px;
    }

    .image {
      width: 60px;
      height: 60px;
      max-width: 60px;
      max-height: 60px;
    }

    button {
      margin: unset;
      width: 20px;
      height: 20px;
      padding: 0;
      border: 0;
      margin-right: 12px;
    }

    .description {
      min-height: unset;
      color: $disabled-text;
    }

    .price {
      justify-content: flex-start;
    }
  }
}
</style>
